import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";
import { Reveal, SectionEyebrow } from "@/components/Reveal";

const Licensing = () => {
  const { data: tiers = [] } = useQuery({
    queryKey: ["tiers"],
    queryFn: async () => (await api.get("/license-tiers")).data,
  });

  return (
    <section id="licensing" className="relative py-24 md:py-32 lg:py-40 border-t border-white/5">
      <div className="absolute top-1/3 left-0 w-[420px] h-[420px] rounded-full bg-[#4DBDBE] opacity-[0.08] blur-[140px] pointer-events-none" />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <SectionEyebrow num="02" label="Licensing" />
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display font-black uppercase tracking-tight leading-[0.95] mt-6 mb-6 text-[clamp(1.6rem,5vw,3.5rem)]">
            License it. <span className="text-vice">Or own it outright.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-white/60 max-w-xl mb-14">
            Four clean tiers. Every purchase ships with a written agreement and instant checkout —
            card, Apple Pay or Google Pay.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((t, i) => {
            const card = (
              <div
                className={`h-full flex flex-col bg-[#0B0B10] border border-white/10 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-[#4DBDBE]/50 hover:shadow-[0_16px_48px_rgba(90,215,214,0.12)] ${
                  t.highlighted ? "shadow-[0_0_48px_rgba(255,255,255,0.06)]" : ""
                }`}
              >
                {t.highlighted && (
                  <span className="self-start text-[10px] uppercase tracking-[0.3em] text-black bg-gradient-to-r from-[#E0E5EC] via-white to-[#9BA3AF] px-3 py-1 mb-4 font-bold">
                    Most popular
                  </span>
                )}
                <h3 className="font-display font-bold uppercase text-lg">{t.name}</h3>
                <div className="font-display text-4xl font-black mt-4">
                  <span className={t.lookup_key === "exclusive" ? "text-vice" : t.highlighted ? "text-chrome" : ""}>
                    ${t.price.toFixed(2)}
                  </span>
                </div>
                <p className="text-sm text-white/60 mt-4 leading-relaxed">{t.tagline}</p>
                <ul className="mt-6 flex flex-col gap-2.5 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-white/75">
                      <Check size={15} className="mt-0.5 shrink-0 text-[#4DBDBE]" />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`licensing-cta-${t.lookup_key}`}
                  onClick={() => scrollToId("vault")}
                  className={`mt-8 w-full py-3.5 text-xs uppercase tracking-widest font-bold transition-all duration-300 ${
                    t.lookup_key === "exclusive"
                      ? "bg-[#D65BB4] hover:shadow-[0_0_32px_rgba(214,91,180,0.5)]"
                      : "border border-white/25 hover:border-[#4DBDBE] hover:text-[#4DBDBE]"
                  }`}
                >
                  Pick a beat in the vault
                </button>
              </div>
            );
            return (
              <Reveal key={t.lookup_key} delay={i * 0.08} className="h-full">
                {t.highlighted ? (
                  <div className="h-full p-[1.5px] bg-gradient-to-br from-[#E0E5EC] via-white to-[#9BA3AF]">{card}</div>
                ) : (
                  card
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Licensing;
